import { useEffect, useRef, useState } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { isBrandedHost, activeHostBrand } from '@/components/HostBranding';
import ManagedConsentScreen, { type ManagedConsentState } from '@/components/ManagedConsentScreen';
import { readableTextOn } from '@/lib/brandColor';
import { isNativeApp } from '@/lib/nativeApp';

// Public surfaces that must work on a branded host WITHOUT org membership:
// the client portal (read-only token link) and the invite-claim flow.
const PUBLIC_PREFIXES = ['/p/', '/accept-invite'];
// Store app pages a signed-out visitor may open besides sign-in.
const NATIVE_OPEN_PREFIXES = ['/pricing', '/terms', '/rodo', '/sso'];
const startsWithAny = (p: string, prefixes: readonly string[]) =>
  prefixes.some((a) => p === a || p.startsWith(a + '/'));

// The only logged-in app surfaces a branded host serves. Anything else (our
// marketing landing, /features, /pricing, /blog, even /cctv-design-tool) bounces
// to /dashboard. Exact-or-subpath match so '/cctv-design' (the designer) is in
// but '/cctv-design-tool' (a marketing page) is NOT.
const APP_PREFIXES = ['/dashboard', '/organization', '/settings', '/checkout-success', '/cctv-design', '/api-docs'];
// Calculators are tools, not marketing: the branded header links them, in any
// language (/pl/calculator/fov).
const TOOL_PREFIXES = ['/calculators', '/calculator', '/en-62676-4-calculator', '/johnson-criteria-calculator'];
export function isAppRoute(p: string): boolean {
  if (APP_PREFIXES.some((a) => p === a || p.startsWith(a + '/'))) return true;
  const bare = p.replace(/^\/[a-z]{2,3}(-[a-z]{2,4})?(?=\/)/i, '');
  return TOOL_PREFIXES.some((a) => bare === a || bare.startsWith(a + '/'));
}

/** Pages the store app shows itself; anything else opens on the website. */
export function isNativeAppPage(p: string): boolean {
  return isAppRoute(p) || startsWithAny(p, NATIVE_OPEN_PREFIXES) || startsWithAny(p, ['/auth', '/login', '/p', '/accept-invite']);
}

/**
 * Branded-host access isolation. On a customer's domain (planner.securitas.se /
 * acme.cctvplanner.io) only the host OWNER and their ACTIVE team members may use
 * the app — anyone else (other companies, free users) is locked out. The public
 * client portal stays open. A no-op on the main domain (cctvplanner.io).
 * Backed by the SECURITY DEFINER RPC my_host_access().
 */
export default function BrandedHostGuard({ children }: { children: React.ReactNode }) {
  const host = isBrandedHost();
  const { user, loading, signOut } = useAuth();
  const location = useLocation();
  const { t } = useTranslation('dashboard');
  const [access, setAccess] = useState<'checking' | 'allowed' | 'denied'>('checking');
  // Partner-managed organisations: consent on first entry, or a seat waiting
  // for an account the person already had (managed_consent_state).
  const [consent, setConsent] = useState<ManagedConsentState | null>(null);
  const [recheck, setRecheck] = useState(0);

  const isPublic = PUBLIC_PREFIXES.some((p) => location.pathname.startsWith(p));
  const isAuthRoute = location.pathname.startsWith('/auth') || location.pathname.startsWith('/login');
  // Keyed on the id, not the user object: every token refresh (hourly, and on
  // returning to the tab) hands useAuth a new object for the same person.
  const userId = user?.id ?? null;
  // Whose verdict `access` holds. Only a different person waits on the
  // loading screen; re-checking the same person keeps the app mounted, since
  // unmounting the designer drops the open project.
  const checkedFor = useRef<string | null>(null);

  useEffect(() => {
    // Signed out: whoever signs in next is checked from scratch.
    if (!userId) checkedFor.current = null;
    if (!host || isPublic || loading || !userId) return;
    let cancelled = false;
    if (checkedFor.current !== userId) setAccess('checking');
    Promise.all([
      supabase.rpc('my_host_access' as never, { p_host: host } as never),
      supabase.rpc('managed_consent_state' as never, { p_host: host } as never),
    ]).then(([hostRes, consentRes]) => {
      if (cancelled) return;
      const state = consentRes.error ? null : (consentRes.data as unknown as ManagedConsentState | null);
      checkedFor.current = userId;
      setConsent(state?.managed ? state : null);
      setAccess(!hostRes.error && hostRes.data === true ? 'allowed' : 'denied');
    });
    return () => { cancelled = true; };
  }, [host, isPublic, userId, loading, recheck]);

  // The store app: like a branded host it serves only the app, minus the host
  // access check. The App Store paywall and the legal pages stay reachable
  // signed out, since App Review opens them from the sign-in screen.
  if (!host && isNativeApp() && !isPublic) {
    const openSignedOut = isAuthRoute || startsWithAny(location.pathname, NATIVE_OPEN_PREFIXES);
    if (loading) return <Centered>{t('common:loading', { defaultValue: 'Loading…' })}</Centered>;
    if (!user) return openSignedOut ? <>{children}</> : <Navigate to={{ pathname: '/auth', search: location.search }} replace />;
    if (!isAppRoute(location.pathname) && !openSignedOut) return <Navigate to="/dashboard" replace />;
    return <>{children}</>;
  }

  // Main domain → no guard. Public portal / accept-invite → always open.
  if (!host || isPublic) return <>{children}</>;

  if (loading) return <Centered>{t('common:loading', { defaultValue: 'Loading…' })}</Centered>;

  // Not signed in → a branded host shows ONLY its login gate. Entering the host
  // (root included) lands straight on /auth — the customer's branded login —
  // never our marketing landing, in a single hop.
  if (!user) {
    if (isAuthRoute) return <>{children}</>;
    // Keep the query: a partner sign-in lands on /<lang>/auth?partner_claim=1.
    return <Navigate to={{ pathname: '/auth', search: location.search }} replace />;
  }

  // Signed in: a branded host serves ONLY the app workspace. Any non-app route
  // (root, /features, /pricing, /blog, /cctv-design-tool, …) → /dashboard.
  if (!isAppRoute(location.pathname) && !isAuthRoute) return <Navigate to="/dashboard" replace />;

  if (access === 'checking') return <Centered>{t('common:loading', { defaultValue: 'Loading…' })}</Centered>;

  const consentScreen = (
    <ManagedConsentScreen
      host={host}
      state={consent ?? { managed: false }}
      onAccepted={() => setRecheck((n) => n + 1)}
      onSignOut={() => signOut()}
    />
  );

  if (access === 'denied' && consent?.claim) return consentScreen;

  if (access === 'denied') {
    const brand = activeHostBrand;
    const color = brand?.primaryColor && /^#[0-9a-f]{6}$/i.test(brand.primaryColor) ? brand.primaryColor : '#1d4ed8';
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-background">
        <div className="max-w-md w-full text-center border rounded-lg p-8" style={{ borderColor: 'var(--color-gray-border, #e5e7eb)' }}>
          {brand?.logoUrl && <img src={brand.logoUrl} alt="" className="h-9 mx-auto mb-4 object-contain" />}
          <h1 className="text-lg font-semibold mb-2" style={{ color }}>
            {t('host_restricted_title', { defaultValue: 'Team access only', })}
          </h1>
          <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-1">
            {t('host_restricted_body', { defaultValue: 'This workspace is restricted to {{name}} team members.', name: brand?.name || 'this company' })}
          </p>
          <p className="text-xs text-neutral-500 mb-6">
            {t('host_restricted_signed_as', { defaultValue: "You're signed in as {{email}}, which isn't a member of this team.", email: user.email })}
          </p>
          <button onClick={() => signOut()} className="px-4 py-2 text-sm rounded" style={{ background: color, color: readableTextOn(color) }}>
            {t('sign_out', { defaultValue: 'Sign out' })}
          </button>
        </div>
      </div>
    );
  }

  if (consent?.role === 'member' && consent.oversight !== false && !consent.consented) return consentScreen;

  return <>{children}</>;
}

function Centered({ children }: { children: React.ReactNode }) {
  return <div className="min-h-screen flex items-center justify-center"><span className="text-sm text-neutral-500">{children}</span></div>;
}
